import React, {
  useState,
  useCallback,
  useMemo,
  useEffect,
  useRef,
} from 'react';
import PropTypes from 'prop-types';
import { sortBy } from 'lodash';
import { validDecimal } from 'utils/accounting-util';
import { useTranslation } from 'react-i18next';
import USER_TYPES from 'constants/UserTypes';
import AuditComments from 'components/modules/comments/AuditComments';
import Alert from 'components/ui/Alert';
import AccessorialRequestStatusBadge from 'components/v2/AccessorialRequestStatusBadge';
import StatHeading from 'components/v2/StatHeading';
import ViewAccessorialRequestDocumentRequirements from 'components/modules/shipments/accessorials/ViewAccessorialDocumentRequirements';
import { Skeleton } from '@cargomatic/ui';

import AccessorialActionButtons from './accessorials/AccessorialActionButtons';
import BulkActionsMenu from './accessorials/BulkActionsMenu';
import EquipmentSelector from './accessorials/EquipmentSelector';
import ShipperRateSection from './accessorials/ShipperRateSection';
import CarrierRateSection from './accessorials/CarrierRateSection';
import AccessorialConfirmModal from './accessorials/AccessorialConfirmModal';
import { getTotal } from './accessorials/accessorialRequestPaneUtils';
import useAccessorialRequestData from './hooks/useAccessorialRequestData';
import useAccessorialMutations from './hooks/useAccessorialMutations';

const AUTO_APPLY_NEEDS_REVIEW_INFO_TEXT_MAPPING = {
  true: 'AUTO_APPLY_NEEDS_REVIEW_INFO_TRUE',
  false: 'AUTO_APPLY_NEEDS_REVIEW_INFO_FALSE',
};

const AccessorialRequestPane = ({
  shipment: shipmentId,
  accessorial: accessorialId,
  onSave,
  dataTestId,
}) => {
  const { t } = useTranslation();

  // Data
  const {
    fullShipment,
    accessorial,
    referenceAccessorial,
    allEquipmentsById,
    accessorialFetched,
    formFields,
    selectedEquipment,
    setFormFields,
    isAccessorialsAdmin,
    isEditable,
    refresh,
  } = useAccessorialRequestData({ shipmentId, accessorialId });

  // Mutations
  const {
    isEditSaving,
    isApproveSaving,
    isDeclineSaving,
    handleSaveButton,
    handleRequestAction,
    handleConfirmBulk,
    handleEquipmentSelect,
  } = useAccessorialMutations({
    accessorial,
    accessorialId,
    formFields,
    fullShipment,
    onSave,
    refresh,
  });

  // UI state
  const [editCarrierRate, setEditCarrierRate] = useState(false);
  const [editShipperRate, setEditShipperRate] = useState(false);
  const [modalState, setModalState] = useState(null);
  const pendingMutationRef = useRef(false);

  // Close modal when mutation completes (saving transitions from true → false)
  useEffect(() => {
    if (pendingMutationRef.current && !isApproveSaving && !isDeclineSaving) {
      pendingMutationRef.current = false;
      setModalState(null);
    }
  }, [isApproveSaving, isDeclineSaving]);

  // Handlers
  const handleEditToggle = useCallback((type) => {
    if (type === USER_TYPES.CARRIER) {
      setEditCarrierRate((prev) => !prev);
    } else {
      setEditShipperRate((prev) => !prev);
    }
  }, []);

  const handleChange = useCallback(
    (field, value) => {
      if (!(field.indexOf('unit_qty') > -1 && !validDecimal(value))) {
        setFormFields((prev) => ({ ...prev, [field]: value }));
      }
    },
    [setFormFields],
  );

  const handleApproveClick = useCallback((type) => {
    setModalState({ mode: 'single', isDecline: false, confirmType: type });
  }, []);

  const handleDeclineClick = useCallback((type) => {
    setModalState({ mode: 'single', isDecline: true, confirmType: type });
  }, []);

  const handleBulkActionMenu = useCallback((event) => {
    setModalState({
      mode: 'bulk',
      isDecline: event === 'decline-all',
      bulkEvent: event,
    });
  }, []);

  const handleModalConfirm = useCallback(
    ({ declineNote, declineReasonCode }) => {
      if (!modalState) return;
      pendingMutationRef.current = true;
      if (modalState.mode === 'single') {
        const action = modalState.isDecline ? 'decline' : 'approve';
        handleRequestAction(
          modalState.confirmType,
          action,
          declineNote,
          declineReasonCode,
        );
      } else {
        handleConfirmBulk(modalState.bulkEvent, declineNote, declineReasonCode);
      }
    },
    [modalState, handleRequestAction, handleConfirmBulk],
  );

  const handleModalHide = useCallback(() => {
    if (!isApproveSaving && !isDeclineSaving) {
      setModalState(null);
    }
  }, [isApproveSaving, isDeclineSaving]);

  const renderActions = useCallback(
    (type) => (
      <AccessorialActionButtons
        accessorial={accessorial}
        accessorialId={accessorialId}
        shipment={fullShipment}
        type={type}
        isAccessorialsAdmin={isAccessorialsAdmin}
        accessorialFetched={accessorialFetched}
        isEditSaving={isEditSaving}
        isApproveSaving={isApproveSaving}
        isDeclineSaving={isDeclineSaving}
        isEditable={isEditable}
        onEdit={handleEditToggle}
        onApprove={handleApproveClick}
        onDecline={handleDeclineClick}
      />
    ),
    [
      accessorial,
      accessorialId,
      fullShipment,
      isAccessorialsAdmin,
      accessorialFetched,
      isEditSaving,
      isApproveSaving,
      isDeclineSaving,
      isEditable,
      handleEditToggle,
      handleApproveClick,
      handleDeclineClick,
    ],
  );

  // Derived values (must be above early return to respect Rules of Hooks)
  const sortedHistory = useMemo(
    () =>
      formFields.history?.length
        ? sortBy(formFields.history, 'timestamp').reverse()
        : [],
    [formFields.history],
  );

  // Loading state
  if (!accessorial || !fullShipment) {
    return (
      <div className="p-6 flex items-center justify-center">
        <Skeleton className="h-4 w-full" />
        <Skeleton className="h-4 w-full" />
        <Skeleton className="h-4 w-full" />
        <Skeleton className="h-4 w-full" />
      </div>
    );
  }

  let payableLabel;
  if (accessorial.payee_type === USER_TYPES.PARTNER) {
    payableLabel = t('PARTNER_REQUESTED_RATE');
  } else if (formFields.vendor) {
    payableLabel = t('VENDOR_REQUESTED_RATE');
  } else {
    payableLabel = t('CARRIER_REQUESTED_RATE');
  }

  const { auto_applied = false, needs_review = false } = accessorial;

  return (
    <div data-testid={dataTestId}>
      <div className="sticky top-0 z-10 bg-background px-6 pt-6 pb-4 border-b">
        <div
          data-testid="div-f1c640a9"
          className="flex items-center justify-between gap-2"
        >
          <h1
            data-testid="h1-685d7482"
            className="inline-flex my-0 text-2xl text-grey-darker min-w-0"
          >
            {formFields.message} ({fullShipment?.shipment_id})
          </h1>
          <div className="flex items-center gap-2 shrink-0">
            <BulkActionsMenu
              accessorial={accessorial}
              shipment={fullShipment}
              isAccessorialsAdmin={isAccessorialsAdmin}
              isEditSaving={isEditSaving}
              isApproveSaving={isApproveSaving}
              isDeclineSaving={isDeclineSaving}
              onBulkAction={handleBulkActionMenu}
            />
          </div>
        </div>
      </div>
      <div className="p-6">
        {auto_applied && (
          <Alert
            dataTestId="Alert-Auto-Apply-Needs-Review-Side-Pane"
            type="info"
          >
            {t(AUTO_APPLY_NEEDS_REVIEW_INFO_TEXT_MAPPING[needs_review])}
          </Alert>
        )}
        <div data-testid="div-6012ab9e" className="flex justify-between my-5">
          <StatHeading
            dataTestId="StatHeading-fb7f618b"
            label={
              accessorial.payer_type === USER_TYPES.CONSIGNEE
                ? t('CONSIGNEE_SUGGESTED_RATE')
                : t('SHIPPER_SUGGESTED_RATE')
            }
            color="green"
            value={getTotal(
              formFields.shipper_rate,
              formFields.shipper_unit_qty,
              accessorial?.currency,
            )}
          />
          <AccessorialRequestStatusBadge status={formFields.shipper_status} />
        </div>
        <ShipperRateSection
          editShipperRate={editShipperRate}
          formFields={formFields}
          referenceAccessorial={referenceAccessorial}
          accessorial={accessorial}
          onChange={handleChange}
          onSave={() => {
            handleSaveButton(USER_TYPES.SHIPPER);
            handleEditToggle(USER_TYPES.SHIPPER);
          }}
          onCancel={() => handleEditToggle(USER_TYPES.SHIPPER)}
          renderActions={() => renderActions(USER_TYPES.SHIPPER)}
        />
        <hr data-testid="hr-b1e5e541" />
        <div data-testid="div-cd8d1d4f" className="flex justify-between mb-5">
          <StatHeading
            dataTestId="StatHeading-825dfaa3"
            label={payableLabel}
            color="green"
            value={getTotal(
              formFields.carrier_rate,
              formFields.carrier_unit_qty,
              accessorial?.currency,
            )}
          />
          <AccessorialRequestStatusBadge status={formFields.carrier_status} />
        </div>
        <CarrierRateSection
          t={t}
          editCarrierRate={editCarrierRate}
          formFields={formFields}
          referenceAccessorial={referenceAccessorial}
          accessorial={accessorial}
          fullShipment={fullShipment}
          onChange={handleChange}
          onSave={() => {
            handleSaveButton(USER_TYPES.CARRIER);
            handleEditToggle(USER_TYPES.CARRIER);
          }}
          onCancel={() => handleEditToggle(USER_TYPES.CARRIER)}
          renderActions={() => renderActions(USER_TYPES.CARRIER)}
        />
        <hr data-testid="hr-0a8894f9" />
        <div data-testid="div-b6546493" className="mb-2">
          <label data-testid="label-b33c90f8" className="form-label">
            {t('EQUIPMENT_CONTAINERS')}:
          </label>
        </div>
        <div data-testid="div-c1b703fb" className="mb-4">
          <EquipmentSelector
            shipmentEquipment={fullShipment?.equipments || []}
            selectedEquipment={selectedEquipment}
            allEquipmentsById={allEquipmentsById}
            onSelect={handleEquipmentSelect}
          />
        </div>
        <hr data-testid="hr-682757bf" />
        <div
          data-testid="div-80d8e84e"
          className="flex flex-col justify-around mt-2"
        >
          <ViewAccessorialRequestDocumentRequirements
            shipment={fullShipment}
            accessorial={{
              ...accessorial,
              slug: accessorial?.accessorial_type,
            }}
            foreignKeys={[accessorial?._id, fullShipment?._id]}
            shipper={fullShipment?.shipper?._id || fullShipment?.shipper}
            isExistingAccessorialRequest={true}
            onUploadComplete={() => {
              refresh();
              onSave?.();
            }}
          />
        </div>
        <hr data-testid="hr-4c2f332e" />
        <div data-testid="div-4ff31917" className="flex justify-between">
          <AuditComments comments={sortedHistory} />
        </div>
        <AccessorialConfirmModal
          show={modalState !== null}
          isBulk={modalState?.mode === 'bulk'}
          isDecline={modalState?.isDecline ?? false}
          confirmType={modalState?.confirmType}
          accessorial={accessorial}
          isLoading={isApproveSaving || isDeclineSaving}
          onConfirm={handleModalConfirm}
          onHide={handleModalHide}
        />
      </div>
    </div>
  );
};

AccessorialRequestPane.propTypes = {
  shipment: PropTypes.string.isRequired,
  accessorial: PropTypes.string.isRequired,
  onSave: PropTypes.func,
  dataTestId: PropTypes.string,
};

export default AccessorialRequestPane;
